// Copyright (c) 2023 embyt GmbH. All rights reserved.
// Author: Marc Planeck

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";

import { IAction, ActionType, raiseAction, IActionTypeSelectGuiItem } from "actions";

import { DashboardPanel } from "types";
import { getPanelSelection, toDropdownItems } from "guiutils";
import { EnergySource, IEnergyMeter, ITariffCircuit } from "parameter";
import { IState } from "states/states";
import { Category } from "states/cachedstates";
import TariffCircuitListModel from "model/tariffcircuitlist";

import Dropdown from "view/comp/Dropdown";

interface ICategorySelectExtProps {
  allowAllEnergySources: boolean;
  allowAllHierarchies: boolean;
  panel: DashboardPanel;
  dashboardIndex?: number;
  className?: string;
  emptyText?: JSX.Element;
  includeChildren?: boolean;
}

interface ICategorySelectProps {
  tariffCircuits: ITariffCircuit[];
  energyMeters: IEnergyMeter[];
  selectedDashboardIndex: number;
  selectedEnergySource: EnergySource | undefined;
  selectedHierarchyId: number | null | undefined;
  selectedCategory: string | null | undefined;
}

const mapStateToProps = (state: IState, ownProps: ICategorySelectExtProps) => {
  const { index, selection } = getPanelSelection(state, ownProps.panel, ownProps.dashboardIndex);
  const props: ICategorySelectProps = {
    tariffCircuits: state.parameter?.tariffline ?? [],
    energyMeters: state.parameter?.energymeter ?? [],
    selectedEnergySource: selection?.energySource,
    selectedHierarchyId: selection?.hierarchyId,
    selectedCategory: selection?.category,
    selectedDashboardIndex: index,
  };
  return props;
};

interface ICategorySelectDispatch {
  onCategoryChanged: (dashboardIndex: number, selection: Category | undefined) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: ICategorySelectDispatch = {
    onCategoryChanged: (dashboardIndex: number, selection: Category | undefined) => {
      const payload: IActionTypeSelectGuiItem = { dashboardIndex, selection };
      dispatch(raiseAction(ActionType.ACT_SELECT_CATEGORY, payload));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  selectCaption: {
    id: "Select category",
  },
});

class CategorySelectComponent extends React.Component<
  ICategorySelectProps & ICategorySelectExtProps & ICategorySelectDispatch & WrappedComponentProps
> {
  private getCategoryName(value: string | null | undefined) {
    if (value === undefined) {
      return <FormattedMessage id="All categories" />;
    } else if (value === null) {
      return (
        <>
          <FormattedMessage id="Without mapping" />
        </>
      );
    }
    return value;
  }

  public render() {
    const tcModel = new TariffCircuitListModel(this.props.tariffCircuits, this.props.energyMeters);
    // in pie-chart view mode, we must not switch to all energy sources
    const categories = tcModel.getCategories(
      this.props.selectedEnergySource,
      this.props.allowAllEnergySources,
      this.props.selectedHierarchyId,
      this.props.allowAllHierarchies,
      this.props.includeChildren ?? false,
    );
    const sourceItems = toDropdownItems(
      categories,
      "selectedCategory",
      (name, value) => this.props.onCategoryChanged(this.props.selectedDashboardIndex, value),
      (value) => this.getCategoryName(value),
    );
    const minCategoriesToShowControl = 2; // all categories, unmapped categories
    return (
      <>
        {categories.length > minCategoriesToShowControl && (
          <div id="categoryselect" className={this.props.className}>
            <Dropdown
              items={sourceItems}
              caption={this.props.intl.formatMessage(messages.selectCaption)}
              curItem={this.getCategoryName(this.props.selectedCategory)}
            />
          </div>
        )}
      </>
    );
  }
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(CategorySelectComponent));
